// ============================================================================
// Editor de tienda — preview en tiempo real.
//
// UN SOLO ORIGEN: el preview NO reimplementa la tienda. Renderiza la MISMA
// capa que el storefront público utiliza:
//   - Marcado: `headerHtml`/`drawerMenuHtml`/`footerHtml` de
//     `sitio-publico/src/js/layout.js` (las 4 variantes incluyen la
//     hamburguesa móvil y el drawer de menú).
//   - Portada: `contenidoHomeHtml` de `sitio-publico/src/js/vistas/home.js`.
//   - Estilos: `sitio-publico/src/styles/main.css` (media queries reales).
//   - Comportamiento: `conectarDrawersMoviles` (apertura/cierre/foco/trap),
//     `aplicarTemaVisual` y `aplicarEstadoVisual` del storefront.
//
// El preview se monta dentro de un <iframe srcdoc> que vive en un VIEWPORT
// VIRTUAL de ancho lógico fijo por dispositivo (1280/768/390px, ver
// `viewport-editor.js`), nunca el ancho físico del panel. El viewport se
// escala visualmente para caber en el editor, pero el layout del iframe
// resuelve contra el ancho lógico: las media queries de `main.css` se
// aplican EXACTAMENTE igual que en la tienda publicada (hamburguesa: solo
// en viewport ≤ 820px, independiente de la variante de header).
//
// La piel del editor (selección por clic) es la ÚNICA capa añadida y es
// puramente decorativa: outline + cursor, sin tocar el diseño.
// ============================================================================

import {
  botonWhatsAppFlotanteHtml,
  escaparHtml,
} from '../../../../sitio-publico/src/js/componentes.js';
import {
  conectarDrawersMoviles,
  drawerMenuHtml,
  footerHtml,
  headerHtml,
} from '../../../../sitio-publico/src/js/layout.js';
import { aplicarEstadoVisual, estadoVisual } from '../../../../sitio-publico/src/js/motortema.js';
import { aplicarTemaVisual } from '../../../../sitio-publico/src/js/tema.js';
import { contenidoHomeHtml } from '../../../../sitio-publico/src/js/vistas/home.js';
// eslint-disable-next-line import/no-unresolved
import storefrontCss from '../../../../sitio-publico/src/styles/main.css?raw';

// El preview NO fabrica contenido demo. El contenido de la portada (bloques,
// banners, categorías y productos) llega DESDE LA BD vía
// `personalizar-tienda.js`; la identidad (nombre, logos, contacto) proviene
// del estado real del editor. Si una sección no tiene datos reales,
// `contenidoHomeHtml` la omite y, si la portada queda vacía, muestra su
// estado vacío elegante — nunca contenido ficticio.
// ---------------------------------------------------------------------------
// Composición de la "tienda" que alimenta el render real del storefront.
// ---------------------------------------------------------------------------

function planNombreEfectivo(empresa) {
  const suscripcion = empresa?.suscripcion?.find((s) => s.estado === 'activa');
  if (suscripcion?.plan?.nombre) return suscripcion.plan.nombre;
  if (empresa?.plan?.nombre) return empresa.plan.nombre;
  if (empresa?.plan_prueba?.nombre) return empresa.plan_prueba.nombre;
  return 'Sin plan';
}

/**
 * Construye la tienda que alimenta `headerHtml`, `contenidoHomeHtml` y
 * `estadoVisual` con datos REALES del negocio: la identidad (nombre, logos,
 * contacto, moneda, estado) proviene del estado del editor; el contenido de
 * la portada (bloques, banners, categorías y productos) llega desde la BD.
 * Sin datos reales no hay sustitución demo: `contenidoHomeHtml` omite la
 * sección y, si la portada queda vacía, muestra su estado vacío.
 * @param {import('./estado-editor.js').EditorState} estado
 * @param {{ bloques: object[], banners: object[], categorias: object[], productos: object[] }} contenido
 */
function construirTienda(estado, contenido) {
  const empresaReal = estado.empresa ?? {};
  const identidad = estado.identidad ?? {};
  const nombre =
    String(identidad.nombre ?? '').trim() || String(empresaReal.nombre ?? '').trim() || 'Mi Tienda';

  return {
    empresa: {
      nombre,
      moneda: empresaReal.moneda ?? 'PEN',
      plan_nombre: planNombreEfectivo(empresaReal),
      estado: empresaReal.estado ?? 'activa',
    },
    perfil: { ...(identidad.perfil ?? {}) },
    bloques: contenido.bloques,
    banners: contenido.banners,
    categorias: contenido.categorias,
    productos: contenido.productos,
    tema: { ...estado.tema },
    editor: { ...estado.editor },
  };
}

// ---------------------------------------------------------------------------
// Documento del iframe (un solo origen: markup, CSS e iconos del storefront)
// ---------------------------------------------------------------------------

const PIEL_CSS = `
  .sp-ed-elemento { cursor: pointer; transition: box-shadow 0.18s ease; }
  .sp-ed-elemento:hover { box-shadow: inset 0 0 0 2px rgba(11, 19, 43, 0.28); }
  .sp-ed-elemento--seleccionado {
    box-shadow: inset 0 0 0 2px rgba(9, 105, 218, 0.55);
  }
  .sp-ed-elemento--seleccionado::after {
    content: 'Seleccionado';
    position: absolute;
    top: 8px;
    right: 8px;
    z-index: 5;
    padding: 3px 9px;
    border-radius: 999px;
    background: rgba(9, 105, 218, 0.9);
    color: #fff;
    font: 600 11px/1.4 system-ui, sans-serif;
    letter-spacing: 0.02em;
    pointer-events: none;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
  }
  .sp-ed-elemento { position: relative; }
`;

function documentoPreviewHtml(tienda, preview) {
  return `<!doctype html>
<html lang="es-PE">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${escaparHtml(tienda.empresa.nombre)} — Vista previa</title>
    <link rel="preconnect" href="https://fonts.googleapis.com" />
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
    <link
      href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Nunito+Sans:wght@400;600;700&display=swap"
      rel="stylesheet"
    />
    <style>${storefrontCss}</style>
    <style>${PIEL_CSS}</style>
  </head>
  <body>
    ${headerHtml(tienda, preview)}
    <div class="sp-overlay" data-sp-overlay hidden></div>
    ${drawerMenuHtml(tienda, preview)}
    <main id="sp-vista">${contenidoHomeHtml(tienda, preview)}</main>
    ${footerHtml(tienda, preview)}
    ${botonWhatsAppFlotanteHtml(tienda)}
  </body>
</html>`;
}

// ---------------------------------------------------------------------------
// Configuración de la preview dentro del iframe (editor skin + drawer real)
// ---------------------------------------------------------------------------

// En la preview los enlaces internos no deben navegar el iframe (el panel es
// el que responde); los externos (target _blank, p. ej. WhatsApp) sí abren.
function aislarNavegacion(doc) {
  doc.addEventListener(
    'click',
    (evento) => {
      const enlace = evento.target.closest('a[href]');
      if (enlace && enlace.target !== '_blank') evento.preventDefault();
    },
    true,
  );
  doc
    .querySelectorAll('form[data-sp-busqueda]')
    .forEach((formulario) =>
      formulario.addEventListener('submit', (evento) => evento.preventDefault()),
    );
}

const MAPA_ELEMENTOS = [
  ['header', '[data-sp-header]', false],
  ['banner', '.sp-hero', false],
  ['categorias', '.sp-categorias', true],
  ['propuesta_valor', '.sp-franja-confianza', false],
  ['testimonios', '.sp-testimonios', true],
  ['productos', '.sp-grid-productos', true],
  ['contacto', '.sp-contacto-resumen', false],
  ['footer', '.sp-footer', false],
];

function vincularSeleccion(doc, seleccionadoId, seleccionar, desplazar = false) {
  doc.querySelectorAll('.sp-ed-elemento--seleccionado').forEach((el) => {
    el.classList.remove('sp-ed-elemento--seleccionado');
    el.removeAttribute('data-seleccion-active');
  });

  let destinoSeleccionado = null;
  for (const [id, selector, usarContenedor] of MAPA_ELEMENTOS) {
    doc.querySelectorAll(selector).forEach((el) => {
      const destino = usarContenedor ? (el.closest('section') ?? el) : el;
      destino.classList.add('sp-ed-elemento');
      destino.dataset.elemento = id;
      destino.addEventListener('click', (evento) => {
        evento.stopPropagation();
        seleccionar(id);
      });
    });
  }

  if (seleccionadoId) {
    doc.querySelectorAll(`[data-elemento="${seleccionadoId}"]`).forEach((el) => {
      el.classList.add('sp-ed-elemento--seleccionado');
      el.setAttribute('data-seleccion-active', '');
      if (desplazar && !destinoSeleccionado) destinoSeleccionado = el;
    });
  }

  if (destinoSeleccionado) {
    // Desplaza el viewport lógico del iframe hasta la sección recién
    // seleccionada (panel -> preview). Indicador visual = resaltado sutil.
    destinoSeleccionado.scrollIntoView({ block: 'center', behavior: 'smooth', inline: 'nearest' });
  }
}

/**
 * Renderiza la preview completa dentro de `contenedorDestino` usando el
 * render real del storefront dentro de un iframe srcdoc.
 *
 * @param {HTMLElement} contenedorDestino - lienzo del dispositivo
 * @param {import('./estado-editor.js').EditorState} estado
 * @param {{ bloques: object[], banners: object[], categorias: object[], productos: object[] }} contenido
 *        - contenido de la portada desde la BD (sin sustitución demo)
 * @param {string} [elementoSeleccionado] - id del elemento seleccionado
 * @param {function(string):void} [seleccionar] - callback al hacer clic en un elemento
 * @param {{desplazar?: boolean}} [opciones] - `desplazar: true` hace que la vista
 *        previa se desplace automáticamente hasta la sección seleccionada y la
 *        deje centrada (panel -> preview).
 */
export function renderPreview(
  contenedorDestino,
  estado,
  contenido = { bloques: [], banners: [], categorias: [], productos: [] },
  elementoSeleccionado = null,
  seleccionar = () => {},
  opciones = {},
) {
  const tienda = construirTienda(estado, contenido);
  const preview = estadoVisual(tienda);
  const desplazar = opciones.desplazar === true;

  let marco = contenedorDestino.querySelector('[data-sp-preview]');
  if (!marco) {
    marco = document.createElement('iframe');
    marco.dataset.spPreview = '';
    marco.className = 'sp-ed-iframe';
    marco.title = 'Vista previa de la tienda';
    marco.setAttribute('aria-label', 'Vista previa de la tienda');
    contenedorDestino.innerHTML = '';
    contenedorDestino.appendChild(marco);
  }

  const pintar = () => {
    const doc = marco.contentDocument;
    const ventana = marco.contentWindow;
    if (!doc || !ventana) return;
    aplicarTemaVisual(tienda.tema, doc.documentElement.style, doc);
    aplicarEstadoVisual(tienda, doc);
    conectarDrawersMoviles({ doc, ventana });
    aislarNavegacion(doc);
    vincularSeleccion(doc, elementoSeleccionado, seleccionar, desplazar);
  };

  marco.onload = pintar;
  marco.srcdoc = documentoPreviewHtml(tienda, preview);
}
